import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  getFiscalYears,
  getMonths,
  getDivisions,
  getTerritories,
  getDashboardData,
  getEmployees,
} from "../../services/aiSalesDiagnosisApi.jsx";
import NudgePanel from "./NudgePanel.jsx";
import { useDashboard } from "./DashboardContext.jsx";
import DashboardPanelSwitcher from "../Common/DashboardPanelSwitcher";
import "./AISalesDiagnosis.css";


function Dashboard() {

  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const {
    filters, setFilters,
    data, setData,
    dropdowns, setDropdowns,
    scrollPosition, setScrollPosition
  } = useDashboard();

  const [yearMonths, setYearMonths] = useState([])

  const months      = dropdowns.months;
  const divisions   = dropdowns.divisions;
  const territories = dropdowns.territories;

  const selectedYearMonth = filters.selectedYearMonth;
  const selectedMonth     = filters.selectedMonth;
  const selectedDivision  = filters.selectedDivision;
  const selectedTerritory = filters.selectedTerritory;

  const dashboardData = data.dashboardData;
  const employees     = data.employees;

  const [totalEmployees, setTotalEmployees] = useState(0)

  const [currentPage, setCurrentPage]     = useState(1)
  const [totalPages, setTotalPages]       = useState(1)
  const pageSize = 10

  const [showNudge, setShowNudge]               = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)

  const [openYearMonth, setOpenYearMonth] = useState(false);
  const [openMonth, setOpenMonth]         = useState(false);
  const [openDivision, setOpenDivision]   = useState(false);
  const [openTerritory, setOpenTerritory] = useState(false);

  // ─── Portal tooltip state ─────────────────────────────────────────────────
  // tooltipState: { key, signal, rect } | null
  const [tooltipState, setTooltipState] = useState(null);
  const tooltipRef = useRef(null);
  const hideTimerRef = useRef(null);

  // ─── Nudge handlers ───────────────────────────────────────────────────────
  const handleNudgeClick = (emp) => { setSelectedEmployee(emp); setShowNudge(true); };
  const handleClose       = () => { setShowNudge(false); setSelectedEmployee(null); };

  // ─── Restore scroll position on mount ────────────────────────────────────
  useEffect(() => {
    window.scrollTo(0, scrollPosition);
    return () => setScrollPosition(window.scrollY);
  }, []);

  // ─── Load fiscal years once on mount ─────────────────────────────────────
  useEffect(() => {
    loadYearMonths();
  }, []);

  const loadYearMonths = async () => {
    try {
      const result = await getFiscalYears();
      setYearMonths(result);
    } catch (err) {
      setError('Failed to load year months');
    }
  };

  // ─── Cascade effects ──────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedYearMonth && months.length === 0) loadMonths();
  }, [selectedYearMonth]);

  const loadMonths = async () => {
    try {
      setLoading(true);
      const result = await getMonths(selectedYearMonth);
      setDropdowns(prev => ({ ...prev, months: result }));
    } catch (err) {
      setError('Failed to load months');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth && divisions.length === 0) loadDivisions();
  }, [selectedMonth]);

  const loadDivisions = async () => {
    try {
      setLoading(true);
      const result = await getDivisions(selectedMonth);
      setDropdowns(prev => ({ ...prev, divisions: result }));
    } catch (err) {
      setError('Failed to load divisions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMonth && selectedDivision && territories.length === 0) loadTerritories();
  }, [selectedMonth, selectedDivision]);

  const loadTerritories = async () => {
    try {
      setLoading(true);
      const result = await getTerritories(selectedMonth, selectedDivision);
      setDropdowns(prev => ({ ...prev, territories: result }));
    } catch (err) {
      setError('Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  // ─── Apply ────────────────────────────────────────────────────────────────
  
  const handleApply = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboardRes, empRes] = await Promise.all([
        getDashboardData(selectedMonth, selectedDivision, selectedTerritory),
        getEmployees(selectedMonth, selectedDivision, selectedTerritory, 1, pageSize), 
      ]);

      setTotalEmployees(empRes.total || 0)
      setData(prev => ({
        ...prev,
        dashboardData: dashboardRes,
        employees: empRes.data || [],
      }));

      
      setCurrentPage(empRes.page || 1);
      setTotalPages(Math.ceil((empRes.total || 0) / pageSize));

    } catch (err) {
      setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Pagination ───────────────────────────────────────────────────────────
  const loadEmployees = async (page) => {
    try {
      setLoading(true);
      const result = await getEmployees(selectedMonth, selectedDivision, selectedTerritory, page, pageSize);
      setData(prev => ({ ...prev, employees: result.data || [] }));
      setTotalEmployees(result.total || 0);
      setCurrentPage(result.page || 1);
      setTotalPages(Math.ceil((result.total || 0) / pageSize));
    } catch (err) {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadEmployees(newPage);
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getPerformanceBadge = (value) => {
    const num = parseFloat(value);
    if (num >= 95) return 'badge-cell badge-high';
    if (num >= 80) return 'badge-cell badge-medium';
    return 'badge-cell badge-low';
  };

  const handleRowClick = (emp) => {
    const q = new URLSearchParams({
      yearmonth: selectedMonth || "",
      division: selectedDivision || "",
      territory: selectedTerritory || "",
    });
    const detailBase = location.pathname.startsWith("/dashboard")
      ? "/dashboard/diagnosis/employee"
      : "/ai-sales-diagnosis/employee";
    navigate(`${detailBase}/emp-${emp.empCode}?${q.toString()}`);
  };

  // ─── Month label helpers ──────────────────────────────────────────────────
  const getPrevMonthLabel = useCallback(() => {
    if (!selectedMonth) return 'PREV MONTH';
    const [year, mon] = selectedMonth.split('-');
    const names = ['','JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
    let m = parseInt(mon) - 1, y = parseInt(year);
    if (m === 0) { m = 12; y -= 1; }
    return `${names[m]} ${String(y).slice(2)}`;
  }, [selectedMonth]);

  const fmtShortMonthYear = (ym) => {
    if (!ym) return '';
    const [year, mon] = ym.split('-');
    const names = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(mon)] || mon} ${String(year).slice(2)}`;
  };




  // ─── Portal tooltip show/hide ─────────────────────────────────────────────
  const handleCardMouseEnter = (e, key, signal) => {
    clearTimeout(hideTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipState({ key, signal, rect });
  };

  const handleCardMouseLeave = () => {
    // small delay so mouse can move INTO the tooltip without it disappearing
    hideTimerRef.current = setTimeout(() => setTooltipState(null), 120);
  };

  const handleTooltipMouseEnter = () => {
    clearTimeout(hideTimerRef.current);
  };

  const handleTooltipMouseLeave = () => {
    hideTimerRef.current = setTimeout(() => setTooltipState(null), 120);
  };

  useEffect(() => () => clearTimeout(hideTimerRef.current), []);


  // ─── 6-Month Trend Bar Chart ──────────────────────────────────────────────
  const TREND_MONTHS = 6;
  const TrendBarChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    const series = data.length > TREND_MONTHS ? data.slice(-TREND_MONTHS) : data;
    const maxVal = Math.max(...series.map((d) => d.value), 1);
    return (
      <div className="trend-chart-wrap">
        <div className="trend-chart-inner">
          {series.map((item, idx) => {
            const isLast = idx === series.length - 1;
            const barHeightPx = Math.max((item.value / maxVal) * 44, 5);
            return (
              <div key={`${item.month}-${idx}`} className={`trend-chart-col${isLast ? " trend-chart-col-current" : ""}`}>
                <span className="trend-chart-label trend-chart-label-top">{item.value}%</span>
                <div
                  className="trend-chart-bar"
                  title={`${item.month}: ${item.value}%`}
                  style={{
                    height: `${barHeightPx}px`,
                    background: isLast
                      ? "linear-gradient(180deg,#00e68a,#00a36c)"
                      : "linear-gradient(180deg,#5dd49a,#3ab87a)",
                    opacity: isLast ? 1 : 0.5,
                  }}
                />
                <span className="trend-chart-label trend-chart-label-bottom">{item.month}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // ─── Portal Tooltip Component ─────────────────────────────────────────────
  const PortalTooltip = () => {
    if (!tooltipState) return null;

    const { key, signal, rect } = tooltipState;
    const c = signal.comparison;
    if (!c) return null;

    const prevLabel = getPrevMonthLabel();
    const currentLabel = fmtShortMonthYear(selectedMonth);
    const isWide = key === 'productMix';
    const tooltipWidth = isWide ? 360 : 280;

    // Position: above the card, horizontally centred on it
    // Clamp so it never goes off-screen left/right
    const vpW = window.innerWidth;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    if (left < 8) left = 8;
    if (left + tooltipWidth > vpW - 8) left = vpW - 8 - tooltipWidth;

    // Place above the card; if not enough room above, place below
    const gap = 10;
    const estimatedHeight = isWide ? 460 : 200;
    let top;
    if (rect.top - gap - estimatedHeight > 8) {
      top = rect.top - gap - estimatedHeight; // above
    } else {
      top = rect.bottom + gap; // below
    }

    // ── Sub-components ──
    const DeltaBadge = ({ delta }) => {
      const num = parseFloat(delta);
      const isPos = num >= 0;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontWeight: '700', fontSize: '13px', color: isPos ? '#00a36c' : '#e53935' }}>
          {isPos ? '↑' : '↓'} {Math.abs(num)}
        </span>
      );
    };

    const MetricBlock = ({ label, current, previous, delta }) => (
      <div style={{ marginBottom: '8px', padding: '8px 10px', background: '#f8f9fa', borderRadius: '7px' }}>
        <div style={{ fontSize: '10px', color: '#888', fontWeight: '700', letterSpacing: '0.4px', marginBottom: '6px', textTransform: 'uppercase' }}>
          {label}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', color: '#bbb', marginBottom: '2px' }}>Current</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#111' }}>{current}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', color: '#bbb', marginBottom: '2px' }}>{prevLabel}</div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#555' }}>{previous}</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '9px', color: '#bbb', marginBottom: '2px' }}>Δ Change</div>
            <DeltaBadge delta={delta} />
          </div>
        </div>
      </div>
    );

    const metricLabelMap = {
      people:     { ffStrength: 'FF Strength', fwd: 'Field Working Days (FWD)' },
      coverage:   { coverage: 'Coverage %', doctorsMet: 'Doctors Met' },
      compliance: { callAverage: 'Call Average', frequencyCompliance: 'Frequency Compliance %' },
    };

    const renderBody = () => {
      if (key === 'productMix') {
        const brands = c.brands;
        const maxSales = brands && brands.length > 0
          ? Math.max(...brands.flatMap(b => [parseFloat(b.currentSales)||0, parseFloat(b.previousSales)||0]), 1)
          : 1;

        return (
          <>
            {c.topProduct && (
              <MetricBlock
                label={`Top Product${signal.metrics?.[0]?.label ? ` — ${signal.metrics[0].label}` : ''}`}
                current={c.topProduct.current}
                previous={c.topProduct.previous}
                delta={c.topProduct.delta}
              />
            )}
            {c.lowestProduct && (
              <MetricBlock
                label={`Lowest Product${signal.metrics?.[1]?.label ? ` — ${signal.metrics[1].label}` : ''}`}
                current={c.lowestProduct.current}
                previous={c.lowestProduct.previous}
                delta={c.lowestProduct.delta}
              />
            )}
            {brands && brands.length > 0 && (
              <div style={{ marginTop: '14px', borderTop: '1px solid #eee', paddingTop: '12px' }}>
                
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#333' }}>
                    BRAND SALES COMPARISON (TOP 50%)
                  </span>

                  <div style={{ display: 'flex', gap: '10px', fontSize: '10px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', background: '#3b6ef8', borderRadius: '2px' }} />
                      {currentLabel}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ width: '10px', height: '10px', background: '#d0d0d0', borderRadius: '2px' }} />
                      {prevLabel}
                    </span>
                  </div>
                </div>

                {/* Bars */}
                {brands.map((brand, idx) => {
                  const current = parseFloat(brand.currentSales) || 0;
                  const previous = parseFloat(brand.previousSales) || 0;

                  const maxValue = Math.max(...brands.map(b =>
                    Math.max(parseFloat(b.currentSales) || 0, parseFloat(b.previousSales) || 0)
                  ), 1);

                  const curWidth = (current / maxValue) * 100;
                  const prevWidth = (previous / maxValue) * 100;

                  return (
                    <div key={idx} style={{ marginBottom: '14px' }}>

                      {/* Brand + % */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <span style={{ fontSize: '12px', fontWeight: '600' }}>
                          {brand.name}
                        </span>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#3b6ef8' }}>
                          {brand.currentPct}%
                        </span>
                      </div>

                      {/* Current bar */}
                      <div style={{
                        height: '10px',
                        background: '#e9ecf5',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '4px'
                      }}>
                        <div style={{
                          width: `${curWidth}%`,
                          height: '100%',
                          background: '#3b6ef8',
                          borderRadius: '4px',
                          transition: 'width 0.4s ease'
                        }} />
                      </div>

                      {/* Previous bar */}
                      <div style={{
                        height: '8px',
                        background: '#f1f1f1',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${prevWidth}%`,
                          height: '100%',
                          background: '#cfcfcf',
                          borderRadius: '4px',
                          transition: 'width 0.4s ease'
                        }} />
                      </div>

                      {/* Values */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '10px',
                        marginTop: '3px'
                      }}>
                        <span style={{ color: '#3b6ef8' }}>{current}L</span>
                        <span style={{ color: '#999' }}>{previous}L</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      }

      
      // Generic: people / coverage / compliance
      const labels = metricLabelMap[key] || {};
      return Object.entries(c).map(([metricKey, v]) => (
        <MetricBlock
          key={metricKey}
          label={labels[metricKey] || metricKey}
          current={v.current}
          previous={v.previous}
          delta={v.delta}
        />
      ));
    };

    return createPortal(
      <div
        ref={tooltipRef}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        style={{
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          width: `${tooltipWidth}px`,
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          padding: '14px 16px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          pointerEvents: 'auto',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid #eee',
        }}>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#222', letterSpacing: '0.5px' }}>
            {signal.title.toUpperCase()}
          </span>
          <span style={{
            fontSize: '10px',
            fontWeight: '700',
            color: '#3b6ef8',
            background: '#eef2ff',
            padding: '2px 8px',
            borderRadius: '10px',
            whiteSpace: 'nowrap',
          }}>
            COMPARISON VS {prevLabel}
          </span>
        </div>

        {/* ── Body ── */}
        {renderBody()}
      </div>,
      document.body
    );
  };

  // const revenue = dashboardData.kpiCards.revenueSurplus.value;
  const revenue = dashboardData?.kpiCards?.revenueSurplus?.value ?? 0;
  let revenueClass = "success";

  if (revenue < 0) {
    revenueClass = "danger";
  } else if (revenue <= 80) {
    revenueClass = "warning";
  }
  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="emcure-ai-sales-diagnosis-embed">
      <header className="header">
        <div className="header-top-row">
          <div className="logo">
            <div className="logo-text">
              <h1>Emcure</h1>
              <p>AI Sales Diagnosis</p>
            </div>
          </div>
          <div className="header-panel-switcher">
            <DashboardPanelSwitcher direction="down" />
          </div>
        </div>

        <div className="header-filter-bar">
          <div className="filter-group">
            <label className="filter-label">YEAR MONTH</label>
            <div className="dropdown">
              <div className="dropdown-header" onClick={() => !loading && setOpenYearMonth(!openYearMonth)}>
                {selectedYearMonth || "Select year month"}
              </div>
              {openYearMonth && (
                <div className="dropdown-list">
                  {yearMonths.map((ym) => (
                    <div key={ym.id} className="dropdown-item" onClick={() => {
                      setDropdowns({ months: [], divisions: [], territories: [] });
                      setFilters(prev => ({ ...prev, selectedYearMonth: ym.id, selectedMonth: "", selectedDivision: "", selectedTerritory: "" }));
                      setOpenYearMonth(false);
                    }}>
                      {ym.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">MONTH</label>
            <div className="dropdown">
              <div className={`dropdown-header ${!selectedYearMonth ? "disabled" : ""}`} onClick={() => { if (!selectedYearMonth || loading) return; setOpenMonth(!openMonth); }}>
                {filters.selectedMonthLabel || "Select month"}
              </div>
              {openMonth && (
                <div className="dropdown-list">
                  {months.map((m, index) => (
                    <div key={m.id || index} className="dropdown-item" onClick={() => {
                      setDropdowns(prev => ({ ...prev, divisions: [], territories: [] }));
                      setFilters(prev => ({
                        ...prev,
                        selectedMonth: m.id || m,
                        selectedMonthLabel: m.label || m,
                        selectedDivision: "",
                        selectedTerritory: ""
                      }));
                      setOpenMonth(false);
                    }}>
                      {m.label || m}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">DIVISION</label>
            <div className="dropdown">
              <div className={`dropdown-header ${!selectedMonth ? "disabled" : ""}`} onClick={() => { if (!selectedMonth || loading) return; setOpenDivision(!openDivision); }}>
                {selectedDivision || "Select division"}
              </div>
              {openDivision && (
                <div className="dropdown-list">
                  {divisions.map((div) => (
                    <div key={div.id} className="dropdown-item" onClick={() => {
                      setDropdowns(prev => ({ ...prev, territories: [] }));
                      setFilters(prev => ({ ...prev, selectedDivision: div.name, selectedTerritory: "" }));
                      setOpenDivision(false);
                    }}>
                      <div className="top-row">
                        <span className="name">{div.name}</span>
                        <span className="emp">{div.employees} emp</span>
                      </div>
                      <div className="bottom-row">
                        <span>₹ {div.salesLakh} L</span>
                        <span className={`achv ${div.achievement >= 100 ? "green" : div.achievement >= 90 ? "orange" : "red"}`}>{div.achievement}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">TERRITORY</label>
            <div className="dropdown">
              <div className={`dropdown-header ${!selectedDivision ? "disabled" : ""}`} onClick={() => { if (!selectedDivision || loading) return; setOpenTerritory(!openTerritory); }}>
                {selectedTerritory || "Select territory"}
              </div>
              {openTerritory && (
                <div className="dropdown-list">
                  {territories.map((ter) => (
                    <div key={ter.id} className="dropdown-item" onClick={() => {
                      setFilters(prev => ({ ...prev, selectedTerritory: ter.name }));
                      setOpenTerritory(false);
                    }}>
                      <div className="top-row">
                        <span className="name">{ter.name}</span>
                        <span className="emp">{ter.employees} emp</span>
                      </div>
                      <div className="bottom-row">
                        <span>₹ {ter.salesLakh} L</span>
                        <span className={`achv ${ter.achievement >= 100 ? "green" : ter.achievement >= 90 ? "orange" : "red"}`}>{ter.achievement}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            className="btn filter-apply-btn"
            onClick={handleApply}
            disabled={!selectedYearMonth || !selectedMonth || !selectedDivision || !selectedTerritory || loading}
          >
            {loading ? 'Loading...' : 'Apply'}
          </button>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      {dashboardData ? (
        <div className="container">
          <div className="territory-header">
            <div className="territory-title">
              <h2>{dashboardData.territoryInfo.hqName}</h2>
              <span className="badge badge-success">{dashboardData.territoryInfo.status}</span>
            </div>
            <div className="territory-info">
              <span>Division: <strong>{dashboardData.territoryInfo.division}</strong></span>
              <span>
                Field Force: <strong>{dashboardData.territoryInfo.actual}/{dashboardData.territoryInfo.total}</strong>
                <span className="danger-text"> ({dashboardData.territoryInfo.vacancies} vacancies)</span>
              </span>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="cards-grid">
            <div className="card">
              <div className="card-header">Sales Achievement</div>
              <div className="card-value success">{dashboardData.kpiCards.salesAchievement.percentage}%</div>
              <div className="card-subtitle">{dashboardData.kpiCards.salesAchievement.actual}L / {dashboardData.kpiCards.salesAchievement.target}L</div>
            </div>

                      

            <div className="card">
              <div className="card-header">Revenue Surplus</div>

              <div className={`card-value ${revenueClass}`}>
                {revenue}
              </div>

              <div className="card-subtitle">
                {revenue > 0 ? 'above target' : revenue < 0 ? 'below target' : 'on target'}
              </div>
            </div>

            <div className="card">
              <div className="card-header">6-Month Trend</div>
              <div className="card-value success" style={{ fontSize: '1.4rem' }}>
                {dashboardData.kpiCards.sixMonthTrend.status === 'Declining' ? '↘' : '↗'}{' '}
                {dashboardData.kpiCards.sixMonthTrend.status}
              </div>
              <TrendBarChart data={dashboardData.kpiCards.sixMonthTrend.data} />
            </div>
          </div>

          {/* Root Cause Signals */}
          <div className="section-header">
            <h3 className="section-title">ROOT CAUSE SIGNALS</h3>
          </div>

          <div className="signal-cards">
            {Object.entries(dashboardData.rootCauseSignals).map(([key, signal]) => (
              <div
                key={key}
                className={`signal-card ${signal.status}`}
                onMouseEnter={(e) => handleCardMouseEnter(e, key, signal)}
                onMouseLeave={handleCardMouseLeave}
              >
                <div className="signal-header">
                  <div className="signal-title">{signal.title}</div>
                  <div style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: signal.status === 'danger' ? '#ff2400' : signal.status === 'success' ? '#00a36c' : '#ffc000'
                  }} />
                </div>
                <div className="signal-metrics">
                  {signal.metrics.map((metric, idx) => (
                    <div key={idx} className="metric-row">
                      <span className="metric-label">{metric.label}</span>
                      <span className="metric-value">{metric.value}</span>
                    </div>
                  ))}
                </div>
                <div className="signal-alerts">
                  {signal.alerts.map((alert, idx) => (
                    <div key={idx} className={
                      signal.status === 'danger' ? 'text-danger'
                      : signal.status === 'success' ? 'text-success'
                      : 'text-warning'
                    }>
                      {alert}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Portal tooltip — renders at document.body, never clipped */}
          <PortalTooltip />

          {/* Employee Table */}
          <div className="section-header">
            {/* <h3 className="section-title">
              REP SCORECARD <span className="section-subtitle">#{employees.length} REP</span>
            </h3> */}
            <h3 className="section-title">
              REP SCORECARD <span className="section-subtitle">#{totalEmployees} REP</span>
            </h3>
            
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Rep</th>
                  <th>FWD [A/T]</th>
                  <th>Dr Met</th>
                  <th>Dr Missed</th>
                  <th>Call Avg</th>
                  <th>Coverage%</th>
                  <th>Compliance%</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id} onClick={() => handleRowClick(emp)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="employee-info">
                        <div className="avatar">
                          {emp.avatar
                            ? <img src={emp.avatar} alt={emp.name} />
                            : emp.name.split(' ').map(n => n[0]).join('')
                          }
                        </div>
                        <div>
                          <div className="employee-name">{emp.name}</div>
                          <div className="employee-code">{emp.empCode}</div>
                        </div>
                      </div>
                    </td>
                    {/* <td>{emp.fwd_actual == null ? "-" : `${emp.fwd_actual}/${emp.fwd_target}`}</td> */}
                    <td>{emp.fwd || "-"}</td>
                    <td>{emp.drMet}</td>
                    <td style={{ color: emp.drMissed > 5 ? '#ff2400' : 'inherit' }}>{emp.drMissed}</td>
                    <td>{emp.callAvg}</td>
                    <td><span className={getPerformanceBadge(emp.coverage)}>{emp.coverage}</span></td>
                    <td><span className={getPerformanceBadge(emp.compliance)}>{emp.compliance}</span></td>
                    <td>
                      <button className="btn" onClick={(e) => { e.stopPropagation(); handleNudgeClick(emp); }}>
                        Nudge
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <NudgePanel show={showNudge} onClose={handleClose} employeeData={selectedEmployee} />

          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn btn-secondary" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}>Previous</button>
              <span>Page {currentPage} of {totalPages}</span>
              <button className="btn btn-secondary" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading}>Next</button>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Please select filters and click Apply to view dashboard data</p>
        </div>
      )}
    </div>
  )
}

export default Dashboard
